// src/hooks/useImportOrders.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImportOrderService } from '../admin/pages/import-orders/importOrderService';
import {
  ImportOrderFormData, ImportOrderStatus, ImportOrderDocumentType, LcStatus,
} from '../admin/pages/import-orders/ImportOrder';

export const PO_KEYS = {
  all: ['importOrders'] as const,
  lists: () => ['importOrders', 'list'] as const,
  paginated: (page: number, search: string, status: string, payment: string) =>
    ['importOrders', 'list', page, search, status, payment] as const,
  stats: () => ['importOrders', 'stats'] as const,
  detail: (id?: string) => ['importOrders', 'detail', id] as const,
};

// Fetches a single order by id — required by both the Detail page and Edit
// mode of the Form page, which (unlike the old drawers) are reached directly
// by URL and can't rely on a prop from an already-fetched list row.
export const useImportOrder = (id?: string) => {
  return useQuery({
    queryKey: PO_KEYS.detail(id),
    queryFn: () => ImportOrderService.getImportOrder(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface POPaginationParams {
  page: number;
  search: string;
  status: string;
  payment: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getImportOrdersPaginated instead
// of cursor objects. Any page can now be fetched in any order.
export const usePurchaseOrdersPaginated = (params: POPaginationParams) => {
  return useQuery({
    queryKey: PO_KEYS.paginated(params.page, params.search, params.status, params.payment),
    queryFn: () =>
      ImportOrderService.getImportOrdersPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        params.status !== 'all' ? (params.status as ImportOrderStatus) : undefined,
        params.payment !== 'all' ? (params.payment as any) : undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

export const usePOStats = () => {
  return useQuery({
    queryKey: PO_KEYS.stats(),
    queryFn: ImportOrderService.getImportOrderStats.bind(ImportOrderService),
    staleTime: 1000 * 60 * 5,
  });
};

export const usePOMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: PO_KEYS.all });
  };

  const create = useMutation({
    mutationFn: ({
      formData,
      userId,
      userName,
    }: {
      formData: ImportOrderFormData;
      userId: string;
      userName: string;
    }) => ImportOrderService.createImportOrder(formData, userId, userName),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: ({
      poId,
      formData,
    }: {
      poId: string;
      formData: ImportOrderFormData;
    }) => ImportOrderService.updateImportOrder(poId, formData),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (poId: string) => ImportOrderService.deleteImportOrder(poId),
    onSuccess: invalidateAll,
  });

  const updateStatus = useMutation({
    mutationFn: ({
      poId,
      status,
      userId,
      userName,
    }: {
      poId: string;
      status: ImportOrderStatus;
      userId: string;
      userName: string;
    }) => ImportOrderService.updateImportOrderStatus(poId, status, userId, userName),
    onSuccess: invalidateAll,
  });

  const updatePayment = useMutation({
    mutationFn: ({
      poId,
      amount,
      userId,
      userName,
    }: {
      poId: string;
      amount: number;
      userId: string;
      userName: string;
    }) => ImportOrderService.recordPayment(poId, amount, userId, userName),
    onSuccess: invalidateAll,
  });

  const markReceived = useMutation({
    mutationFn: ({
      poId,
      items,
      userId,
      userName,
    }: {
      poId: string;
      items: any[];
      userId: string;
      userName: string;
    }) => ImportOrderService.markImportOrderAsReceived(poId, items, userId, userName),
    onSuccess: invalidateAll,
  });

  const uploadDocuments = useMutation({
    mutationFn: ({
      poId,
      files,
    }: {
      poId: string;
      files: { file: File; type: ImportOrderDocumentType }[];
    }) => ImportOrderService.uploadImportOrderDocuments(poId, files),
    onSuccess: invalidateAll,
  });

  const updateLc = useMutation({
    mutationFn: ({
      poId,
      lcNumber,
      lcStatus,
      lcOpenedDate,
    }: {
      poId: string;
      lcNumber: string;
      lcStatus: LcStatus;
      lcOpenedDate: Date | null;
    }) => ImportOrderService.updateLcDetails(poId, lcNumber, lcStatus, lcOpenedDate),
    onSuccess: invalidateAll,
  });

  return { create, update, remove, updateStatus, updatePayment, markReceived, uploadDocuments, updateLc };
};
